<script lang="ts" setup>
const emit = defineEmits<{
  done: []
  notifyChildAppTracker: []
}>()

const isModalOpen = defineModel<boolean>({ required: true })

const { t } = useI18n()
const { data: config } = useQueryConfig.current()
const supportEmail = computed(() => config.value?.support_email || 'support@docue.com')
const { mutateAsync: sendContactRequest, isPending, status } = useMutationHubspot.sendContactRequest()
const { trackSubmitContactForm } = useTracker()
const { success } = useToast()
const { notify } = useBugsnag()

const id = useId()
const formId = `${id}-contact-us-form`

const message = ref('')

const close = () => {
  isModalOpen.value = false
}

const handleOpenUpdate = (open: boolean) => {
  if (isPending.value)
    return

  isModalOpen.value = open
}

// reset message when modal is opened
watchEffect(() => {
  if (isModalOpen.value)
    message.value = ''
})

const submit = async () => {
  try {
    await sendContactRequest({
      payload: {
        message: message.value,
      },
    })

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('is-child-app') !== 'true')
      trackSubmitContactForm()
    else
      emit('notifyChildAppTracker')

    emit('done')
    close()
    success(t('contact-us-modal.success-message'))
  }
  catch (error) {
    if (error instanceof Error)
      notify(error)
  }
}
</script>

<template>
  <UiDialog
    :open="isModalOpen"
    @update:open="handleOpenUpdate"
  >
    <UiDialogContent
      align="top"
      class="
        max-h-svh w-[calc(100%-2rem)] max-w-2xl gap-0 overflow-hidden p-0
        [&>button]:left-auto [&>button]:right-4
      "
    >
      <UiDialogHeader class="gap-2 px-6 pb-4 pr-16 pt-6 text-left sm:px-7 sm:pr-16 sm:pt-7">
        <UiDialogTitle
          size="lg"
          class="mx-0"
        >
          {{ t('contact-us-modal.title') }}
        </UiDialogTitle>
        <UiDialogDescription class="max-w-xl text-sm leading-6">
          <i18n-t keypath="contact-us-modal.description">
            <template #supportEmail>
              <UiButton
                variant="link-inline"
                color="primary"
                class="underline"
                as-child
              >
                <NuxtLink
                  :to="`mailto:${supportEmail}`"
                  external
                >
                  {{ supportEmail }}
                </NuxtLink>
              </UiButton>
            </template>
          </i18n-t>
        </UiDialogDescription>
      </UiDialogHeader>

      <ErrorAlert
        v-if="status === 'error'"
        class="mx-6 mb-4 sm:mx-7"
        :error="t('contact-us-modal.error')"
      />

      <form
        :id="formId"
        class="flex min-h-0 flex-col px-6 pb-6 sm:px-7 sm:pb-7"
        @submit.prevent="submit"
      >
        <UiTextarea
          v-model="message"
          autofocus
          :aria-label="t('message')"
          :disabled="isPending"
          :placeholder="t('contact-us-modal.placeholder')"
          class="
            min-h-56 resize-none rounded-xl border-neutral-200 px-4 py-3 text-base
            leading-6 shadow-sm
          "
          required
          rows="8"
        />

        <div class="mt-4 flex justify-end">
          <UiButton
            color="primary"
            size="md"
            type="submit"
            :disabled="isPending || !message.trim().length"
            :loading="isPending"
          >
            {{ t('send') }}
          </UiButton>
        </div>
      </form>
    </UiDialogContent>
  </UiDialog>
</template>
